<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.airline') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="code"
        :label="$t('attributes.code')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.code }}</p>
      </b-table-column>

      <b-table-column
        field="picture"
        :label="$t('attributes.picture')"
        width="80"
        sortable
        centered
        v-slot="props"
      >
        <figure v-if="props.row.picture" class="image">
          <img
            :src="AirlineImage(props.row.picture)"
            :alt="$t('attributes.picture')"
            style="width: 50px; margin: auto;"
          />
        </figure>
        <p v-else class="has-text-danger has-text-weight-medium">無</p>
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        field="english_name"
        :label="$t('attributes.english_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.english_name }}
      </b-table-column>

      <b-table-column
        field="contact_person"
        :label="$t('attributes.contact_person')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.contact_person }}
      </b-table-column>

      <b-table-column
        field="res_tel"
        :label="$t('attributes.reservation_phone')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.res_tel }}
      </b-table-column>

      <b-table-column
        field="address"
        :label="$t('attributes.address')"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.address }}
      </b-table-column>

      <b-table-column :width="80" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <airline-form :airline="selectedItem" @send="onSend" />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { AIRLINE_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import AirlineForm from './AirlineForm'

export default {
  name: 'AirlineList',
  components: { AirlineForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      searchName: this.query.search || '',
      sortField: this.query.sort || 'code',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      isFormModalActive: false,
      sending: false
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${AIRLINE_API}`
        const params = {
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('airline', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.searchName = query.search || ''
      this.sortField = query.sort || 'code'
      this.sortOrder = query.order || 'asc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10
    },
    updateQuery() {
      const query = {
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilter: debounce(function() {
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },

    onAdd() {
      this.selectedItem = {
        code: null,
        name: '',
        english_name: '',
        res_tel: '',
        contact_person: '',
        address: '',
        picture: ''
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.code == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(AIRLINE_API, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${AIRLINE_API}/${item.code}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.code === _item.code)
        const items = [...this.items]
        items[idx] = _item
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
